// eslint-disable-next-line import/no-restricted-paths -- server-only module
import getServerConfig from 'Config/serverConfig';
import { logger } from 'Services/logger/logger';
import { convertToError } from 'Utilities/error/convertToError';

import {
	_resetServerOptimizelyClientForTesting,
	configureServerOptimizelyClient,
	getServerOptimizelyClient,
	getServerOptimizelyReadiness,
} from './serverOptimizelyClient';

const SCOPE = 'serverOptimizely';
const DEFAULT_DECISION_READY_TIMEOUT_MS = 2_000;

configureServerOptimizelyClient({
	get: (key) => getServerConfig(key),
	logger,
});

const getDecisionReadyTimeoutMs = (): number => {
	const raw: unknown = getServerConfig('optimizely_decision_ready_timeout_ms');
	const timeout = typeof raw === 'string' && raw !== '' ? Number(raw) : raw;

	if (typeof timeout === 'number' && Number.isFinite(timeout) && timeout >= 0) {
		return timeout;
	}

	return DEFAULT_DECISION_READY_TIMEOUT_MS;
};

export { getServerOptimizelyClient };

/**
 * Make a server-side Optimizely feature flag decision.
 *
 * Returns `true`/`false` for real decisions, or `null` when no reliable
 * decision could be made (client unavailable, datafile not yet loaded, etc.).
 *
 * When the client was pre-warmed with a datafile (the normal startup path),
 * decisions are served immediately. The readiness wait only applies to the
 * lazy-init fallback, where the SDK must download the datafile first.
 */
export const getServerOptimizelyDecision = async (
	flagKey: string,
	userId: string,
	attributes?: Record<string, string | boolean | number | null>,
): Promise<{ enabled: boolean | null; variationKey: string | null }> => {
	try {
		const instance = await getServerOptimizelyClient();

		if (!instance) return { enabled: null, variationKey: null };

		const readiness = getServerOptimizelyReadiness();
		if (!readiness.isClientReady && readiness.readyPromise) {
			const decisionTimeout = getDecisionReadyTimeoutMs();
			let timeoutId: ReturnType<typeof setTimeout> | null = null;

			type ReadyResult = { ready: boolean; reason: 'onReady' } | { ready: false; reason: 'timeout' };

			const timeoutPromise = new Promise<ReadyResult>((resolve) => {
				timeoutId = setTimeout(() => resolve({ ready: false, reason: 'timeout' }), decisionTimeout);
			});

			const onReadyResult: Promise<ReadyResult> = readiness.readyPromise
				.then((): ReadyResult => ({ ready: getServerOptimizelyReadiness().isClientReady, reason: 'onReady' }))
				.finally(() => {
					if (timeoutId !== null) clearTimeout(timeoutId);
				});

			const readyResult = await Promise.race([onReadyResult, timeoutPromise]);

			if (!readyResult.ready) {
				const message =
					readyResult.reason === 'timeout'
						? `Optimizely not ready within ${decisionTimeout}ms — skipping decision for flag=${flagKey}`
						: `Optimizely client failed to become ready — skipping decision for flag=${flagKey}`;

				logger.info({ message, scope: SCOPE });
				return { enabled: null, variationKey: null };
			}
		}

		const userContext = instance.createUserContext(userId, attributes);

		if (!userContext) {
			logger.warn({ message: `Failed to create user context for userId=${userId}`, scope: SCOPE });
			return { enabled: null, variationKey: null };
		}

		const decision = userContext.decide(flagKey);

		logger.debug({
			context: {
				...(attributes && { attributes }),
				enabled: decision.enabled,
				flagKey,
				userId,
				variationKey: decision.variationKey,
			},
			message: `Flag decision: ${flagKey}=${decision.enabled}`,
			scope: SCOPE,
		});

		return { enabled: decision.enabled, variationKey: decision.variationKey };
	} catch (error) {
		logger.error({
			error: convertToError(error),
			message: `Optimizely decision failed for flag=${flagKey}, userId=${userId}`,
			scope: SCOPE,
		});
		return { enabled: null, variationKey: null };
	}
};

/** Reset the singleton — only for use in tests. @internal */
// eslint-disable-next-line no-underscore-dangle
export const _resetForTesting = (): void => {
	_resetServerOptimizelyClientForTesting();
};
